import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { FACEBOOK_WWW_HOST } from '@mpr/shared';

import { api, ApiError, type ConversationDto } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { ChoiceFilter, DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { Icon } from './Icons.js';

/**
 * The messages screen: a list of people down one side, one exchange open beside
 * it.
 *
 * The inbox this sits next to is a *queue* — everything owed, newest first,
 * across every Page. That is the right shape for working through a backlog and
 * the wrong one for talking to somebody: a customer who wrote three times
 * appeared as three unrelated rows, and whoever answered had no idea the first
 * two had happened.
 *
 * So this screen is built the other way round. The unit is the person, the
 * transcript reads oldest first like a transcript, and one reply answers the
 * whole exchange rather than the single message it was aimed at.
 *
 * The post a comment sits under is a *title* here, not content. The rule the
 * owner set is that somebody answering comments sees the post only as context
 * for the comment — so it is one line at the top, and pressing it opens the
 * post rather than putting it in the way of the conversation.
 */
/**
 * What to call this conversation.
 *
 * Usually the person: their name is what somebody is looking for down a list.
 *
 * But a conversation with no participant is not an anonymous stranger — on this
 * account every one of them turned out to be a thread made only of the Page's
 * own comments, with no customer in it at all. Calling that "Someone" invents a
 * person who was never there; naming it by the post says what it is.
 */
function titleOf(
  row: { participantName: string | null; postExcerpt: string | null },
  fallback: string,
): string {
  if (row.participantName) return row.participantName;

  /*
   * A few words of the post, never the post.
   *
   * The excerpt is a paragraph. Used whole it ran across the conversation's
   * header and sat on top of the way back and the expand control — the
   * controls were still there, underneath the text, and unreachable. A title
   * is a handful of words by definition; the post itself is one press away
   * behind its own button.
   */
  const excerpt = row.postExcerpt?.trim();
  if (excerpt) return excerpt.length > 34 ? `${excerpt.slice(0, 34)}…` : excerpt;

  return fallback;
}

export function ConversationsPanel({
  openId: requested,
  onOpened,
}: {
  /** A conversation the inbox asked to open, or null. */
  openId: string | null;
  /** Called once it has been taken, so it is not reopened on every visit. */
  onOpened: () => void;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const queryClient = useQueryClient();

  const [pageId, setPageId] = useState('');
  const [channel, setChannel] = useState('');
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  /*
   * Which pane a phone is showing.
   *
   * Two panes stacked on a 390px screen gives a list nobody can scan above a
   * conversation nobody can read. Every messaging application shows one or the
   * other and a way back, so this does too — and on a wide screen the class is
   * inert, because there both panes fit side by side.
   */
  const [onThread, setOnThread] = useState(false);

  /*
   * Full screen, on a phone, remembered.
   *
   * The chrome around an open conversation measures 161px — a header, a row of
   * tabs and the section bar — which is a fifth of a tall phone and a quarter
   * of a short one, spent on furniture in the screen where the day's work
   * happens. This lifts the conversation over all of it.
   *
   * Remembered because it is a preference, not a gesture: somebody who works
   * this way works this way every time, and making them ask again on every
   * conversation is the kind of small tax that ends with the feature unused.
   * Read defensively — a browser with storage blocked must not take the screen
   * down with it.
   */
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem('mpr.chat.expanded') === '1';
    } catch {
      return false;
    }
  });

  const setExpandedAndRemember = (value: boolean): void => {
    setExpanded(value);
    try {
      localStorage.setItem('mpr.chat.expanded', value ? '1' : '0');
    } catch {
      // A preference that cannot be saved is still a preference for this visit.
    }
  };


  /*
   * The channels to filter by: the ones this installation offers, and Facebook
   * comments always. An Instagram account is not a Page, so it has no row in the
   * Page filter beside this — the channel is how "only Instagram" is asked for.
   */
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  // `me.channels` also carries permission categories (ACCOUNT, COMMENTS, WEBHOOKS...),
  // which are not conversation channels and have no label here.
  const offeredChannels = [
    'PAGE_COMMENT',
    ...(me.data?.channels ?? []).filter((key) => ['MESSENGER', 'INSTAGRAM', 'WHATSAPP'].includes(key)),
  ];

  const list = useQuery({
    queryKey: ['conversations', pageId, channel, waitingOnly],
    queryFn: () =>
      api.conversations({
        pageId: pageId || undefined,
        channel: channel || undefined,
        waiting: waitingOnly,
      }),
    // Somebody sitting on this screen is waiting for the next customer, so it
    // polls. The open conversation polls on its own, faster.
    refetchInterval: 15_000,
  });

  const rows = list.data?.conversations ?? [];

  /*
   * Open the first conversation, once.
   *
   * An empty right-hand pane on arrival reads as a broken screen rather than as
   * an invitation, and the first row is what somebody would have pressed. Only
   * while nothing is open, so a poll never steals the conversation being read.
   */
  useEffect(() => {
    if (!openId && rows[0]) setOpenId(rows[0].id);
  }, [openId, rows]);

  /*
   * A conversation the inbox sent us to.
   *
   * Takes precedence over the first row, and on a phone opens the thread
   * directly — arriving on the list after asking for one exchange in
   * particular is the wrong place to land.
   */
  useEffect(() => {
    if (!requested) return;
    setOpenId(requested);
    setOnThread(true);
    onOpened();
  }, [requested, onOpened]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('conversations.title')}</h2>
        <div className="inline-form">
          <ChoiceFilter
            value={channel}
            onChange={setChannel}
            allLabel={t('inbox.allChannels')}
            options={offeredChannels.map((key) => ({
              value: key,
              label: t(`inbox.channel.${key}` as TranslationKey),
            }))}
          />
          <DestinationFilter
            feature={DestinationFeature.INBOX}
            value={pageId ? pageDestinationId(pageId) : ''}
            allLabel={t('inbox.allPages')}
            onChange={(id) => setPageId(pageIdOf(id))}
          />
          <label className="check-row">
            <input
              type="checkbox"
              checked={waitingOnly}
              onChange={(event) => setWaitingOnly(event.target.checked)}
            />
            <span className="tiny">
              {t('conversations.waitingOnly', { count: list.data?.waiting ?? 0 })}
            </span>
          </label>
        </div>
      </div>

      {list.isLoading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <Icon.comment />
          <p>{t('conversations.empty')}</p>
        </div>
      ) : (
        <div
          className={['chat', onThread ? 'on-thread' : '', expanded ? 'expanded' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <nav className="chat-list" aria-label={t('conversations.title')}>
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                className={row.id === openId ? 'chat-row active' : 'chat-row'}
                onClick={() => {
                  setOpenId(row.id);
                  setOnThread(true);
                }}
              >
                <div className="chat-row-head">
                  <strong>{titleOf(row, t('conversations.someone'))}</strong>
                  <span className="tiny muted">{fmt.dateTime(row.lastMessageAt)}</span>
                </div>
                <div className="chat-row-line">
                  {/* Our own last word is marked, so a glance down the list
                      separates "answered" from "waiting for me". */}
                  {row.lastFromPage && (
                    <span className="tiny muted">{t('conversations.you')} </span>
                  )}
                  <span className="tiny">{row.lastMessage ?? t('common.noText')}</span>
                </div>
                <div className="chat-row-meta">
                  {/* Where this came from. A comment on a post and a private
                      message are answered differently and cost different
                      mistakes, and the row never said which it was. */}
                  <span className={`chan chan-${row.channel.toLowerCase()}`}>
                    {t(`inbox.channel.${row.channel}` as TranslationKey)}
                  </span>
                  <span className="badge">{row.pageName}</span>
                  {row.waitingSince && (
                    <span className="badge warn">
                      {t('conversations.waitingSince', { when: fmt.dateTime(row.waitingSince) })}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </nav>

          {openId ? (
            <Transcript
              conversationId={openId}
              expanded={expanded}
              onToggleExpanded={() => setExpandedAndRemember(!expanded)}
              onBack={() => setOnThread(false)}
              onReplied={() => {
                void queryClient.invalidateQueries({ queryKey: ['conversations'] });
                void queryClient.invalidateQueries({ queryKey: ['inbox'] });
              }}
            />
          ) : (
            <div className="chat-empty">{t('conversations.pick')}</div>
          )}
        </div>
      )}
    </section>
  );
}

/** One exchange, read as a transcript, with the reply box under it. */
function Transcript({
  conversationId,
  expanded,
  onToggleExpanded,
  onBack,
  onReplied,
}: {
  conversationId: string;
  /** Whether this conversation currently owns the whole screen. */
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Back to the list. Only reachable on a phone, where one pane shows at a time. */
  onBack: () => void;
  onReplied: () => void;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  /** The offered draft this reply started from, kept through editing. */
  const [fromSuggestion, setFromSuggestion] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  /**
   * The reply box starts one line tall and grows with what is typed.
   *
   * It was two rows always — about 120px of the screen held permanently for a
   * message that is usually one line, on the screen with the least room to
   * spare. Capped so a pasted paragraph cannot push the send button off a
   * phone; past the cap the box scrolls, which is what every messaging app
   * does.
   */
  const fit = (el: HTMLTextAreaElement | null): void => {
    if (!el) return;
    // Reset first: a textarea's scrollHeight never shrinks on its own, so
    // measuring without this makes the box grow and never come back.
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  };

  useEffect(() => fit(box.current), [draft]);
  const [notice, setNotice] = useState<string | null>(null);
  const [showPost, setShowPost] = useState(false);
  const foot = useRef<HTMLDivElement>(null);

  const detail = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.conversation(conversationId),
    refetchInterval: 8_000,
  });

  const items = detail.data?.items ?? [];
  const conversation = detail.data?.conversation;

  /*
   * Scroll to the newest message when the conversation changes, and only then.
   * Scrolling on every poll would yank the page out from under somebody reading
   * back through the history.
   */
  useEffect(() => {
    foot.current?.scrollIntoView({ block: 'end' });
  }, [conversationId]);

  /**
   * A reply is aimed at the newest thing the customer said.
   *
   * A comment reply has to attach to a specific comment — Meta has no notion of
   * replying to a thread — and the newest is the one being answered. For
   * messages any item in the exchange resolves to the same recipient, so the
   * same choice is simply harmless there.
   */
  /*
   * What a reply answers: the newest thing the customer said.
   *
   * The fallback skips our own rows too. A reply of ours is not a Meta object —
   * it carries no comment id — so answering "it" would post nowhere.
   */
  const target =
    [...items].reverse().find((item) => !item.fromPage) ??
    [...items].reverse().find((item) => !item.ours);

  /*
   * The newest draft, and only that one.
   *
   * They used to stack: every suggestion an agent had ever written for this
   * exchange, listed above the transcript. Three of them looked like a
   * conversation of their own that had started somewhere else, and whoever was
   * answering had to work out which was the current offer. One offer at a time
   * is what an offer is.
   */
  const offered = [...items]
    .reverse()
    .flatMap((item) => item.suggestions)
    .find((suggestion) => suggestion.reply);

  /*
   * Dismissed by hand, or the moment it is acted on.
   *
   * Keyed by the draft's id rather than a bare boolean, so a *new* suggestion
   * arrives visible instead of inheriting the last one's dismissal.
   */
  const [hiddenDraftId, setHiddenDraftId] = useState<string | null>(null);
  const suggestion = offered && offered.id !== hiddenDraftId ? offered : null;

  /**
   * Ask the Page's own assistant to write this one.
   *
   * The draft lands in the reply box rather than going out: the assistant
   * proposes and a person presses send, which is the same bargain the inbox
   * makes. Aimed at the same item a reply would be, so the assistant is
   * answering the thing actually being answered.
   */
  const ask = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('nothing to answer');
      return api.suggestInboxReply(target.id);
    },
    onSuccess: (data) => {
      if (data.suggestion.reply) {
        // The new one replaces the old on screen rather than joining it.
        setHiddenDraftId(null);
        setNotice(null);
      } else if (data.suggestion.error) {
        /*
         * It did not decline — it never answered. A broken endpoint, a timeout,
         * an HTTP error from the agent itself: that is not the assistant's
         * judgement about this message, and reporting it as one sent operators
         * looking at their inbox for a problem that is in Automation.
         */
        setNotice(t('conversations.assistantFailed', { reason: data.suggestion.error }));
      } else {
        // A refusal is information. The assistant read it and would not answer,
        // which is worth saying rather than leaving the box empty.
        setNotice(t('conversations.assistantDeclined', { outcome: data.suggestion.outcome }));
      }
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
    onError: (error: unknown) =>
      setNotice(error instanceof ApiError ? error.message : t('common.requestFailed')),
  });

  const send = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('nothing to answer');
      return api.replyToInboxItem(target.id, draft.trim(), {
        fromSuggestionId: fromSuggestion ?? undefined,
      });
    },
    onSuccess: () => {
      setDraft('');
      setFromSuggestion(null);
      setNotice(null);
      // Answered. Whatever was being offered is no longer an offer.
      if (offered) setHiddenDraftId(offered.id);
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      onReplied();
    },
    onError: (error: unknown) =>
      setNotice(error instanceof ApiError ? error.message : t('common.requestFailed')),
  });

  if (detail.isLoading) return <div className="loading">{t('common.loading')}</div>;
  if (!conversation) return <div className="chat-empty">{t('conversations.pick')}</div>;

  return (
    <div className="chat-thread">
      <header className="chat-head">
        <div className="chat-who">
          {/* Hidden on a wide screen by CSS: there the list never went away. */}
          <button type="button" className="chat-back ghost small" onClick={onBack}>
            {t('conversations.back')}
          </button>
          <div>
            <strong>{titleOf(conversation, t('conversations.someone'))}</strong>
            <span className="tiny muted"> · {conversation.pageName}</span>
          </div>

          {/*
            The rest of the post, next to the piece of it being used as a title.
            
            It belongs here and not among the controls on the other side: those
            two decide where a person is — the way out and the way to full
            screen — and they keep their size and their place whatever this
            conversation is called. This one is about the title beside it, so it
            sits with it and shrinks with it.
          */}
          {conversation.externalPostId && (
            <button
              type="button"
              className="chat-post ghost small"
              onClick={() => setShowPost(true)}
              title={conversation.postExcerpt ?? t('conversations.thePost')}
            >
              {t('conversations.more')}
            </button>
          )}
          <span className={`chan chan-${conversation.channel.toLowerCase()}`}>
            {t(`inbox.channel.${conversation.channel}` as TranslationKey)}
          </span>
        </div>

        {/*
          Full screen and back, on a phone.

          Beside the way out rather than off in a corner: expanding hides the
          section bar, so the two controls that decide where a person is need
          to be in the same place. Hidden on a wide screen, where the chrome
          costs a fraction of what it costs here.
        */}
        <button
          type="button"
          className="chat-expand ghost small"
          onClick={onToggleExpanded}
          aria-pressed={expanded}
          title={expanded ? t('conversations.restore') : t('conversations.expand')}
        >
          {expanded ? '⤡' : '⤢'}
        </button>
      </header>

      {showPost && <PostPeek conversation={conversation} onClose={() => setShowPost(false)} />}

      <div className="chat-messages">
        {items.map((item) => (
          <article
            key={item.id}
            className={item.fromPage ? 'bubble ours' : 'bubble theirs'}
            aria-label={item.fromPage ? t('conversations.you') : (item.authorName ?? '')}
          >
            <p>{item.message ?? t('common.noText')}</p>
            <span className="tiny muted">{fmt.dateTime(item.createdAt)}</span>
          </article>
        ))}
        <div ref={foot} />
      </div>

      {notice && <div className="notice error">{notice}</div>}

      {/*
        The offer, directly above the box it would fill.

        It sits here and not above the transcript because it is not something
        that was said — it is a proposal waiting on a decision, and the decision
        is made at the keyboard. Three ways out and no fourth: take it, drop it,
        or ask for a different one. Any of them ends it.
      */}
      {suggestion && (
        <div className="chat-offer">
          <div className="chat-offer-head">
            <span className="badge tiny">
              {suggestion.source === 'agent'
                ? t('inbox.fromAgent', { name: suggestion.sourceName ?? '' })
                : t('inbox.fromAssistant', { name: suggestion.sourceName ?? '' })}
            </span>
          </div>

          <p>{suggestion.reply}</p>

          <div className="actions">
            <button
              type="button"
              className="small"
              onClick={() => {
                setDraft(suggestion.reply as string);
                setFromSuggestion(suggestion.id);
                setHiddenDraftId(suggestion.id);
              }}
            >
              {t('inbox.useDraft')}
            </button>
            <button
              type="button"
              className="ghost small"
              onClick={() => ask.mutate()}
              disabled={ask.isPending}
            >
              {ask.isPending ? t('conversations.asking') : t('conversations.askAnother')}
            </button>
            <button
              type="button"
              className="ghost small"
              onClick={() => setHiddenDraftId(suggestion.id)}
            >
              {t('common.dismiss')}
            </button>
          </div>
        </div>
      )}

      <div className="chat-compose">
        <textarea
          ref={box}
          rows={1}
          value={draft}
          placeholder={t('conversations.placeholder')}
          onChange={(event) => setDraft(event.target.value)}
        />
        {/*
          Writing and asking sit side by side, because they are two ways of
          doing the same job and somebody switches between them mid-thought.

          The words are carried by `title` on a phone and shown beside the icon
          on a wider screen — see `.chat-compose` in the stylesheet. Two labelled
          buttons under a text box cost a row of their own on a 390px screen,
          and neither icon needs a caption to be understood next to a message
          box.
        */}
        <button
          type="button"
          className="ghost"
          onClick={() => ask.mutate()}
          disabled={ask.isPending || !target}
          title={t('conversations.askAssistant')}
          aria-label={t('conversations.askAssistant')}
        >
          <Icon.robot />
          <span className="label">
            {ask.isPending ? t('conversations.asking') : t('conversations.askAssistant')}
          </span>
        </button>
        <button
          type="button"
          onClick={() => send.mutate()}
          disabled={send.isPending || draft.trim().length === 0 || !target}
          title={t('conversations.send')}
          aria-label={t('conversations.send')}
        >
          <Icon.send />
          <span className="label">
            {send.isPending ? t('conversations.sending') : t('conversations.send')}
          </span>
        </button>
      </div>
    </div>
  );
}

/**
 * The post a comment thread sits under, in a panel over the conversation.
 *
 * Deliberately thin: the opening line we already hold, and a way through to the
 * post itself on Facebook. Fetching the whole post would spend a Graph call per
 * conversation opened, on context that the excerpt usually settles.
 */
function PostPeek({
  conversation,
  onClose,
}: {
  conversation: ConversationDto;
  onClose: () => void;
}) {
  const { t } = useI18n();

  /*
   * Meta's post id is "<page>_<post>", and the permalink wants the halves
   * apart. An id in any other shape is left alone rather than guessed at — a
   * broken link is worse than no link.
   */
  const [page, post] = (conversation.externalPostId ?? '').split('_');
  const href = page && post ? `${FACEBOOK_WWW_HOST}/${page}/posts/${post}` : null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <h3>{t('conversations.thePost')}</h3>
        <p>{conversation.postExcerpt ?? t('common.noText')}</p>

        <div className="actions">
          {href && (
            <a href={href} target="_blank" rel="noreferrer" className="ghost small">
              {t('conversations.openOnFacebook')}
            </a>
          )}
          <button type="button" className="ghost small" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
